import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import SignInDialog from '../../components/common/SignInDialog';
import api from '../../redux/features/auth/axiosAuthHelper';
import UserDetailCard from '../../components/common/cards/UserDetailCard';
import RecordDetailDialog from '../../components/common/RecordDetailDialog';
import UsersTable from '../../components/common/tables/UsersTable';
import { useUsersTable } from './useUsersTable';

const UsersPage = () => {
  const authData = useSelector((state: RootState) => state.auth);
  const table = useUsersTable(authData.isAuthenticated, authData.access);
  const [user, setUser] = useState<any>(null);
  const [popup, setPopup] = useState('');
  const [showGlobalFilter, setShowGlobalFilter] = useState(true);
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({ user_type: false });
  const { paramID } = useParams<{ paramID: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const openedFromTable = useRef<string | null>(null);
  useEffect(() => {
    if (location.state?.fromTable) {
      openedFromTable.current = paramID ?? null;
      navigate(location.pathname, { replace: true, state: null });
      return;
    }
    if (paramID && openedFromTable.current === paramID) {
      openedFromTable.current = null;
      return;
    }
    openedFromTable.current = null;
    if (paramID?.toLowerCase() === 'create') {
      setPopup('Create');
      setUser(null);
    } else if (paramID && /^\d+$/.test(paramID)) {
      let active = true;
      api.get(`users/${paramID}/`, {
        headers: { Authorization: `Bearer ${authData.access}` },
      }).then(response => {
        if (!active) return;
        setUser(response.data);
        setPopup('Edit');
      }).catch(error => {
        if (!active) return;
        setUser({ id: paramID, email: 'Not Found' });
        setPopup('Edit');
        console.error('Error fetching user:', error);
      });
      return () => { active = false; };
    } else if (paramID) {
      navigate('/users', { replace: true });
    }
  }, [paramID, location.pathname, location.state, navigate, authData.access]);

  const handleRefreshButton = () => {
    setUser(null);
    setPopup('');
    navigate('/users', { replace: true });
    table.refresh();
  };

  const handleBackButton = () => {
    setUser(null);
    setPopup('');
    navigate('/users', { replace: true });
  };

  const openEditPage = (row: any) => {
    setUser(row.original);
    setPopup('Edit');
    navigate(`/users/${row.original.id}`, { replace: true, state: { fromTable: true } });
  };

  const openCreatePage = () => {
    setUser(null);
    setPopup('Create');
    navigate('/users/create', { replace: true });
  };

  return authData.isAuthenticated === false ? (
    <SignInDialog isAuthenticated={authData.isAuthenticated} />
  ) : authData.isAuthenticated ? (
    <div data-testid="div-9bus" style={{ overflowX: "hidden", maxHeight: `100%`, minHeight: `100%`, maxWidth: `100%`, minWidth: `100%`, margin: "auto" }}>

      <Box sx={{ p: { xs: 1, md: 1.5 }, height: '100dvh', minHeight: 360, boxSizing: 'border-box', minWidth: 0 }}>
                    <UsersTable
                      rows={table.rows}
                      rowCount={table.rowCount}
                      isLoading={table.isLoading}
                      hasLoaded={table.hasLoaded}
                      error={table.error}
                      refresh={table.refresh}
                      sorting={table.sorting}
                      columnVisibility={columnVisibility}
                      columnFilters={table.columnFilters}
                      pagination={table.pagination}
                      globalFilter={table.globalFilter}
                      rowSelection={table.rowSelection}
                      setRowSelection={table.setRowSelection}
                      showGlobalFilter={showGlobalFilter}
                      setShowGlobalFilter={setShowGlobalFilter}
                      handleRemoveAllFilters={table.clearFilters}
                      setGlobalFilter={table.onGlobalFilterChange}
                      setSorting={table.onSortingChange}
                      setColumnVisibility={setColumnVisibility}
                      setColumnFilters={table.onColumnFiltersChange}
                      setPagination={table.onPaginationChange}
                      handleRefreshButton={handleRefreshButton}
                      openEditPage={openEditPage}
                      openCreatePage={openCreatePage}
                    />
      </Box>
      <RecordDetailDialog open={(!!user && popup === 'Edit') || popup === 'Create'} title={popup === 'Create' ? 'Create User' : 'Edit User'} onClose={handleBackButton} compactHeader>
        {popup === 'Create' ? (
          <UserDetailCard handleRefreshButton={handleRefreshButton} handleBackButton={handleBackButton} />
        ) : user ? (
          <UserDetailCard user_id={user.id} handleRefreshButton={handleRefreshButton} handleBackButton={handleBackButton} onStatusChanged={table.refresh} />
        ) : null}
      </RecordDetailDialog>
    </div>

  ) : (null);
};

export default UsersPage;
